/**
 * useRecaptcha Hook
 * Provides reCAPTCHA v3 integration for bot protection
 * Uses module-level cache to prevent duplicate edge function calls across components
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface UseRecaptchaReturn {
  isReady: boolean;
  isConfigured: boolean;
  isLoading: boolean;
  executeRecaptcha: (action: string) => Promise<string | null>;
  verifyToken: (token: string, action: string) => Promise<{ success: boolean; score?: number }>;
}

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';

// Module-level cache to deduplicate across all hook instances
let cachedSiteKeyPromise: Promise<{ siteKey: string | null; configured: boolean }> | null = null;

function fetchSiteKeyOnce(): Promise<{ siteKey: string | null; configured: boolean }> {
  if (!cachedSiteKeyPromise) {
    cachedSiteKeyPromise = supabase.functions.invoke('get-recaptcha-site-key')
      .then(({ data, error }) => {
        if (error) {
          logger.warn('[useRecaptcha] Failed to fetch site key:', error.message);
          return { siteKey: null, configured: false };
        }
        if (data?.configured && data?.siteKey) {
          return { siteKey: data.siteKey as string, configured: true };
        }
        return { siteKey: null, configured: false };
      })
      .catch((err) => {
        logger.warn('[useRecaptcha] Error fetching site key:', err);
        // Allow retry on next mount if it was a transient error
        cachedSiteKeyPromise = null;
        return { siteKey: null, configured: false };
      });
  }
  return cachedSiteKeyPromise;
}

export function useRecaptcha(enabled = true): UseRecaptchaReturn {
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const initAttemptedRef = useRef(false);

  // Load reCAPTCHA script dynamically
  const loadRecaptchaScript = useCallback((key: string) => {
    if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setIsReady(true);
          setIsLoading(false);
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => {
        setIsReady(true);
        setIsLoading(false);
      });
    };

    script.onerror = () => {
      logger.warn('[useRecaptcha] Failed to load reCAPTCHA script');
      setIsReady(false);
      setIsLoading(false);
    };

    document.head.appendChild(script);
  }, []);

  // Fetch site key using shared promise only when a protected form is active.
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    if (initAttemptedRef.current) return;
    initAttemptedRef.current = true;

    fetchSiteKeyOnce().then((result) => {
      if (result.configured && result.siteKey) {
        setSiteKey(result.siteKey);
        setIsConfigured(true);
        loadRecaptchaScript(result.siteKey);
      } else {
        setIsConfigured(false);
        setIsLoading(false);
      }
    });
  }, [enabled, loadRecaptchaScript]);

  const executeRecaptcha = useCallback(async (action: string): Promise<string | null> => {
    if (!isConfigured || !siteKey) return null;
    if (!isReady || !window.grecaptcha) {
      logger.warn('[useRecaptcha] reCAPTCHA not ready');
      return null;
    }

    try {
      return await window.grecaptcha.execute(siteKey, { action });
    } catch (err) {
      logger.warn('[useRecaptcha] Failed to execute reCAPTCHA:', err);
      return null;
    }
  }, [isConfigured, isReady, siteKey]);

  const verifyToken = useCallback(async (
    token: string, 
    action: string
  ): Promise<{ success: boolean; score?: number }> => {
    if (!token) return { success: true };

    try {
      const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
        body: { token, action }
      });

      if (error) {
        logger.warn('[useRecaptcha] Verification error:', error.message);
        return { success: false };
      }

      return { 
        success: data?.success ?? false,
        score: data?.score as number | undefined
      };
    } catch (err) {
      logger.warn('[useRecaptcha] Failed to verify token:', err);
      return { success: false };
    }
  }, []);

  return { isReady, isConfigured, isLoading, executeRecaptcha, verifyToken };
}
